/**
 * components/wizard/SanityMultiZoneBlock.jsx — Phase E v8c.5.
 *
 * Multi-zone management block rendered inside the Sanity panel of
 * the calibration wizard. Composed of:
 *   • <FieldCoverageSilhouette/> — top-down preview of every zone +
 *     convex-hull coverage score (visual anchor, ALWAYS visible in
 *     the Sanity phase even when zero extra zones exist yet — the
 *     base rectangle's footprint alone gives the operator immediate
 *     spatial feedback).
 *   • List of extra zones with kind-aware remove buttons.
 *   • "➕ Weiteren Bereich kalibrieren" CTA.
 *
 * Extracted from `Step2Calibration.jsx` (Phase E v8c.5 tech-debt
 * cleanup, 2026-02-28) so the parent file drops back under the
 * 1600-LOC guideline cap.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { getPreset } from "@/lib/fieldPresets";
import FieldCoverageSilhouette from "./FieldCoverageSilhouette";

export default function SanityMultiZoneBlock({
  fmt = "Outdoor",
  basePresetId,
  baseSide,
  fieldProfile,
  extraZones = [],
  onAddZone,
  onRemoveZone,
}) {
  return (
    <div
      data-testid="cal-multi-zone-block"
      className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-3 space-y-3"
    >
      {/* 🆕 v8c.5 · Always-visible coverage silhouette. Even with zero
          extra zones the operator gets immediate visual feedback on
          how much of the pitch the base rectangle alone covers. */}
      <FieldCoverageSilhouette
        fmt={fmt}
        fieldProfile={fieldProfile}
        basePresetId={basePresetId}
        baseSide={baseSide}
        extraZones={extraZones}
      />

      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          Zusätzliche Kalibrierungs-Bereiche · {extraZones.length}
        </div>
        {extraZones.length > 0 && (
          <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
            {extraZones.length + 1} Zone{extraZones.length > 0 ? "n" : ""} gesamt
          </span>
        )}
      </div>

      {extraZones.length === 0 ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          Möchtest du weitere Bereiche kalibrieren, um die Genauigkeit
          zu erhöhen? Pässe außerhalb der Basis-Zone extrapolieren
          sonst — füge z.B. die gegenüberliegende Brick-Zone oder das
          halbe Spielfeld hinzu, damit die Homographie das gesamte
          Pitch abdeckt.
        </p>
      ) : (
        <ul className="space-y-1.5 mt-1" data-testid="cal-extra-zones-list">
          {extraZones.map((z, i) => {
            const p = getPreset(fmt, z.preset_id);
            // 🆕 Anchor-Tag v1 (2026-06) · Ring-Semantik:
            //   joint → grüner Ring (trägt zur Homographie bei)
            //   visual_marker_only → grauer Ring (nur Overlay-Marker)
            //   source="anchor_collect" → amber Badge (Anker-Sammelmodus)
            const isolated = z.status === "visual_marker_only";
            const fromAnchorMode = z.source === "anchor_collect";
            const rowCls = isolated
              ? "flex items-center justify-between gap-2 px-2 py-1.5 "
                + "bg-card/50 border border-zinc-500/50 ring-1 ring-zinc-500/30 rounded-sm"
              : "flex items-center justify-between gap-2 px-2 py-1.5 "
                + "bg-emerald-500/5 border border-emerald-400/50 ring-1 ring-emerald-400/30 rounded-sm";
            return (
              <li
                key={`${z.preset_id}-${i}`}
                data-testid={`cal-extra-zone-row-${i}`}
                data-zone-status={z.status || "joint"}
                className={rowCls}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] text-foreground truncate flex items-center gap-1.5">
                    <span>{p?.labelDe || z.preset_id}</span>
                    {z.side && (
                      <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-widest">
                        · {z.side}
                      </span>
                    )}
                    {isolated && (
                      <span
                        data-testid={`cal-extra-zone-visual-marker-${i}`}
                        className="font-mono text-[8px] uppercase tracking-widest px-1 py-px rounded-sm bg-zinc-500/20 text-zinc-300 border border-zinc-500/50"
                        title="Diese Zone wurde wegen Kamerabewegung aus dem Joint-Solve isoliert und wird nur visuell mitgeführt."
                      >
                        Visual Marker
                      </span>
                    )}
                    {fromAnchorMode && (
                      <span
                        data-testid={`cal-extra-zone-anchor-badge-${i}`}
                        className="font-mono text-[8px] uppercase tracking-widest px-1 py-px rounded-sm bg-amber-500/20 text-amber-200 border border-amber-400/50"
                        title={`Aus dem Anker-Sammelmodus (Data-Flywheel)${z.combo_id ? ` · Kombination: ${z.combo_id}` : ""}`}
                      >
                        ⚓ Anker-Modus
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[9px] text-muted-foreground tabular-nums">
                    {z.image_points?.length || 0} pts · t={z.at_time_s?.toFixed(2) ?? "0"}s
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`cal-extra-zone-remove-${i}`}
                  onClick={() => onRemoveZone?.(i)}
                  className="px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-rose-300 hover:bg-rose-500/15 rounded-sm border border-rose-400/40"
                  title="Diese Zone entfernen"
                >
                  Entfernen
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Button
        data-testid="cal-add-zone-btn"
        variant="outline"
        size="sm"
        onClick={onAddZone}
        className="w-full mt-2 border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500 hover:text-black text-emerald-300 font-display font-bold uppercase tracking-widest text-[10px]"
      >
        ➕ Weiteren Bereich kalibrieren
      </Button>
    </div>
  );
}
